import React, { useCallback, useState } from "react";
import { Table } from "reactstrap";
import { Icon } from "../Icon/Icon";

export type RowData = {
  id: string | number;
  [key: string]: unknown;
};

export type TableColumn<TItem extends RowData> = {
  id?: string | number;
  title?: React.ReactNode;
  isSortable?: boolean;
  onSort?: (...args: unknown[]) => void;
  colSpan?: number;
  isWithoutHeaderTitle?: boolean;
  align?: React.TdHTMLAttributes<HTMLTableCellElement>["align"];
  value?: keyof TItem | string;
  render?: (item: TItem) => React.ReactNode;
};

type HeadCellProps = {
  title?: React.ReactNode;
  isSortable?: boolean;
  onSort?: (...args: unknown[]) => void;
  colSpan?: number;
};

const HeadCell = ({
  title,
  isSortable = false,
  onSort,
  colSpan,
}: HeadCellProps) => {
  const [isOrderDescending, setIsOrderDescending] = useState(true);

  const handleClick = useCallback(
    (...args: unknown[]) => {
      onSort?.(...args);
      setIsOrderDescending((prev) => !prev);
    },
    [onSort],
  );

  if (!isSortable) {
    return <th colSpan={colSpan}>{title}</th>;
  }

  return (
    <th colSpan={colSpan}>
      <div className="d-flex justify-content-between">
        {title}
        <button
          onClick={handleClick}
          className="mx-1 p-0 border-0 bg-transparent text-gray-700"
        >
          <Icon
            iconName={
              isOrderDescending ? "chevron-down-regular" : "chevron-up-regular"
            }
          />
        </button>
      </div>
    </th>
  );
};

type BodyRowProps<TItem extends RowData> = {
  item: TItem;
  columns: Array<TableColumn<TItem>>;
};

const BodyRow = <TItem extends RowData>({
  item,
  columns,
}: BodyRowProps<TItem>) => (
  <tr key={item.id}>
    {columns.map((column, index) => {
      const columnKey = column.id ?? index;

      if (column.render) {
        return (
          <td align={column.align} key={columnKey}>
            {column.render(item)}
          </td>
        );
      }

      if (typeof column.value === "string" && item[column.value]) {
        return (
          <td align={column.align} key={columnKey}>
            {item[column.value] as React.ReactNode}
          </td>
        );
      }

      return <td align={column.align} key={columnKey} />;
    })}
  </tr>
);

type TableCustomProps<TItem extends RowData> = {
  columns: Array<TableColumn<TItem>>;
  data: TItem[];
};

export const TableCustom = <TItem extends RowData>({
  columns,
  data,
}: TableCustomProps<TItem>) => (
  <Table>
    <thead>
      <tr>
        {columns
          .filter((column) => !column.isWithoutHeaderTitle)
          .map(({ title, isSortable, onSort, colSpan }, index) => (
            <HeadCell
              title={title}
              isSortable={isSortable}
              onSort={onSort}
              key={index}
              colSpan={colSpan}
            />
          ))}
      </tr>
    </thead>
    <tbody>
      {data.map((item) => (
        <BodyRow key={item.id} item={item} columns={columns} />
      ))}
    </tbody>
  </Table>
);
